import { Component, OnInit, OnDestroy, inject, signal, effect, untracked, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatStepperModule } from '@angular/material/stepper';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';
import { Subject, takeUntil } from 'rxjs';

import { ProjectSettingsPageComponent } from '../../pages/project-settings-page/project-settings-page.component';
import { FeatureDialogService } from '../feature-dialog.service';
import { AppStateService } from '../../shared/services/app-state/app-state.service';
import { SecretPickerComponent } from '@app/shared/components/secret-picker/secret-picker.component';
import {
  Connection,
  ConnectionType,
  ObjectStorageConnection,
  ObjectStorageAuthType,
  ObjectStorageProvider,
  DatabaseType,
  LLMType,
  OAuthCredential,
} from '../../shared/interfaces';
import { StorageGrant, grantCovers, grantLocation } from '@app/shared/interfaces/storage-grant.interface';
import { BoxOperation } from '../../shared/interfaces/connection.interface';
import { ConnectionFormStore } from './connection-form.store';
import {
  type ConnectionBasics,
  type StandardConnectionKey,
  STANDARD_CONNECTION_DESCRIPTORS,
  objectStorageConfigToForm,
} from './connection-form.mapper';
import {
  OBJECT_STORAGE_VALIDATORS,
  SERVICE_VALIDATORS,
  applyConditionalValidators,
} from './connection-form.validators';
import { parseServerDate } from '../../shared/helpers/date-formatting.helper';

export interface AddConnectionDialogData {
  projectId: number;
  mode?: 'create' | 'edit';
  connection?: Connection | ObjectStorageConnection;
}

export type SupportedConnectionType = 'database' | 'datafusion' | 'llm' | 'box' | 'objectstorage' | 'service';

@Component({
  selector: 'app-add-connection-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCardModule,
    MatIconModule,
    MatStepperModule,
    MatProgressSpinnerModule,
    MatTabsModule,
    MatTooltipModule,
    MatChipsModule,
    SecretPickerComponent,
  ],
  templateUrl: './add-connection-dialog.component.html',
  styleUrls: ['./add-connection-dialog.component.scss'],
  providers: [ConnectionFormStore],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddConnectionDialogComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private dialog = inject(MatDialog);
  private dialogRef = inject<MatDialogRef<AddConnectionDialogComponent>>(MatDialogRef);
  data = inject<AddConnectionDialogData>(MAT_DIALOG_DATA);
  private appState = inject(AppStateService);
  private featureDialogService = inject(FeatureDialogService);
  /** Owns init-data, save/test orchestration, and the OAuth handshake. Public for the template. */
  readonly store = inject(ConnectionFormStore);
  private destroy$ = new Subject<void>();
  private initialFormSnapshot: string | null = null;
  private postLoadSnapshotTaken = false;

  // Form groups (kept in the component — the store deliberately owns no forms)
  connectionBasicsForm!: FormGroup;
  databaseForm!: FormGroup;
  dataFusionForm!: FormGroup;
  llmForm!: FormGroup;
  boxForm!: FormGroup;
  objectStorageForm!: FormGroup;
  serviceForm!: FormGroup;

  // ── Thin re-exports of store signals, so the template reads them unchanged ──
  readonly globalServices = this.store.globalServices;
  readonly boxApps = this.store.boxApps;
  readonly oauthProviders = this.store.oauthProviders;
  readonly oauthCredentials = this.store.oauthCredentials;
  readonly isLoadingOAuthProviders = this.store.isLoadingOAuthProviders;
  readonly isLoadingOAuthCredentials = this.store.isLoadingOAuthCredentials;
  readonly isInitiatingOAuth = this.store.isInitiatingOAuth;
  readonly selectedOAuthProviderConfigId = this.store.selectedOAuthProviderConfigId;

  // Local UI state
  isSaving = signal(false);
  isTesting = signal(false);
  /** True when editing a legacy (pre-registry) Box connection, to show a deprecation notice. */
  isLegacyBoxConnection = signal(false);
  error = signal<string | null>(null);
  testResult = signal<{ success: boolean; message: string } | null>(null);
  selectedTabIndex = signal<number | null>(null);
  readonly databaseTypes = Object.values(DatabaseType);
  readonly llmTypes = Object.values(LLMType);
  readonly boxOperations: BoxOperation[] = ['list', 'download', 'upload', 'uploadVersion', 'delete', 'move', 'mkdir', 'comment'];
  readonly objectStorageProviders = [
    ObjectStorageProvider.S3,
    ObjectStorageProvider.AWS,
    ObjectStorageProvider.GCS,
    ObjectStorageProvider.Azure,
  ];
  readonly isEditMode = this.data.mode === 'edit' && !!this.data.connection;

  constructor() {
    this.buildForms();
    // Initialize the store (and its init-data resources) BEFORE registering the
    // effects below, so they never observe the pre-init idle state.
    this.store.init(this.data.projectId);

    // SCRUM-451: re-snapshot once the init data has settled (never the pre-load
    // idle state), so async form mutations don't register as user edits.
    //
    // The global-services fallback MUST live in this same effect, before the
    // snapshot. Splitting them leaves the two unordered: box apps start loading
    // in the constructor and so usually settle first, which would snapshot a
    // form still in 'global' mode and only then flip it to 'custom' — leaving
    // hasFormChanges() true and popping "Leave connection setup?" on a form the
    // user never touched. That is exactly the SCRUM-451 regression, and it
    // would hit anyone opening a project with no enabled global services.
    //
    // Waiting on globalServicesSettled() costs nothing when the Service tab is
    // never opened: it is already true while the list is unrequested. And on
    // the service-edit path ensureGlobalServices() runs synchronously in
    // ngOnInit, before the first effect flush, so the wait is registered in
    // time rather than racing the resource.
    effect(() => {
      // Fall back to custom mode when the global-services list settles empty
      // (same behaviour as the old init-time flip, but only after
      // ensureGlobalServices — EHQ-255). Deliberately NOT behind the
      // postLoadSnapshotTaken guard: the Service tab can be opened long after
      // the initial snapshot, and that load must still flip the mode or the
      // user is left on an empty global-service dropdown with no way forward.
      if (
        this.store.globalServicesRequested() &&
        this.store.globalServicesSettled() &&
        this.globalServices().length === 0 &&
        this.serviceForm.get('mode')?.value === 'global'
      ) {
        this.serviceForm.get('mode')?.setValue('custom');
      }

      if (
        !this.store.initDataSettled() ||
        !this.store.globalServicesSettled() ||
        this.postLoadSnapshotTaken
      ) {
        return;
      }

      this.postLoadSnapshotTaken = true;
      this.snapshotFormState();
    });

    // Approved grants load after the form (edit mode, or a slow list): re-run
    // the prefix rule once they arrive.
    effect(() => {
      this.store.approvedStorageGrants();
      untracked(() => this.revalidatePrefix());
    });

    // If OAuth providers resolve empty while OAuth is selected, fall back to a
    // non-OAuth auth type (OAuth is only offered when providers exist). Never on
    // an edit: the auth type is locked there (EHQ-460), and the providers list is
    // still [] while it loads, so the fallback would silently change an OAuth
    // connection's auth type and the server would refuse the save.
    effect(() => {
      const providers = this.oauthProviders();
      const authType = this.objectStorageForm.get('authType');
      if (this.isEditMode || authType?.disabled) {
        return;
      }
      if (providers.length === 0 && authType?.value === ObjectStorageAuthType.OAuth) {
        const provider = this.objectStorageForm.get('provider')?.value as ObjectStorageProvider;
        const fallback = this.getProviderAuthTypes(provider).find((t) => t !== ObjectStorageAuthType.OAuth);
        if (fallback) {
          this.objectStorageForm.patchValue({ authType: fallback });
        }
      }
    });
  }

  ngOnInit(): void {
    if (this.isEditMode) {
      this.initializeEditMode();
    }
    this.snapshotFormState();
  }

  get dialogTitle(): string {
    return this.isEditMode ? 'Edit Connection' : 'Create Connection';
  }

  /**
   * The "create secrets beforehand" banner is only relevant when the active
   * form references a pre-created secret (SCRUM-623). Registry-backed Box
   * connections and DataFusion never do; ObjectStorage and Service only for
   * auth modes that take a secret id.
   */
  get showSecretsBanner(): boolean {
    switch (this.connectionBasicsForm.get('type')?.value) {
      case 'database':
      case 'llm':
        return true;
      case 'objectstorage': {
        const authType = this.objectStorageForm.get('authType')?.value;
        return (
          authType === ObjectStorageAuthType.AccessKey ||
          authType === ObjectStorageAuthType.ServiceAccount
        );
      }
      case 'service':
        return (
          this.serviceForm.get('mode')?.value === 'custom' &&
          this.serviceForm.get('authType')?.value !== 'none'
        );
      default:
        // datafusion, box (registry — the Box app holds the secret), or no type yet
        return false;
    }
  }

  get saveButtonLabel(): string {
    return this.isEditMode ? 'Save' : 'Create';
  }

  get savingButtonLabel(): string {
    return this.isEditMode ? 'Saving...' : 'Creating...';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildForms(): void {
    this.connectionBasicsForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(1)]],
      // Type can't be changed when editing; disable at control-creation time
      // (not via a template `[disabled]` binding, which warns on reactive forms).
      type: [{ value: '', disabled: this.isEditMode }, [Validators.required]],
      description: [''],
    });

    this.databaseForm = this.fb.group({
      databaseType: [DatabaseType.PostgreSQL, [Validators.required]],
      host: ['', [Validators.required]],
      port: ['5432', [Validators.required, Validators.pattern(/^\d+$/)]],
      username: ['', [Validators.required]],
      dbname: ['', [Validators.required]],
      passwordSecretId: ['', [Validators.required]],
      options: [''],
    });

    // Update default port when database type changes
    this.databaseForm.get('databaseType')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((dbType) => {
      const defaultPorts = ['5432', '1433'];
      const currentPort = this.databaseForm.get('port')?.value;
      if (defaultPorts.includes(currentPort)) {
        const newPort = dbType === DatabaseType.SQLServer ? '1433' : '5432';
        this.databaseForm.patchValue({ port: newPort }, { emitEvent: false });
      }
    });

    // No controls: the server ignores a DataFusion connection's url (EHQ-359).
    this.dataFusionForm = this.fb.group({});

    this.llmForm = this.fb.group({
      llmType: [LLMType.OpenAI, [Validators.required]],
      apiKeySecretId: ['', [Validators.required]],
      endpoint: [''],
    });

    // Registry model (SCRUM-614): a Box connection references a platform-managed
    // Box app and a root folder; raw credentials are no longer entered here.
    this.boxForm = this.fb.group({
      boxAppId: ['', [Validators.required]],
      rootFolderId: ['', [Validators.required]],
      allowedOperations: [[]],
    });

    this.objectStorageForm = this.fb.group({
      provider: [ObjectStorageProvider.S3, [Validators.required]],
      authType: [ObjectStorageAuthType.AccessKey, [Validators.required]],
      bucket: ['', [Validators.required]],
      endpoint: [''],
      region: [''],
      projectId: [''],
      accountName: [''],
      accessKey: [''],
      secretId: [''],
      userCredentialId: [''],
      // Keyless (EHQ-427): the approved grant, and a prefix inside it.
      storageGrantId: [''],
      prefix: ['', [(control: AbstractControl) => this.keylessPrefixValidator(control)]],
    });

    this.serviceForm = this.fb.group({
      mode: ['global', [Validators.required]], // 'global' or 'custom'
      globalServiceId: [''],
      url: [''],
      timeoutMs: [''],
      authType: ['none'], // 'none', 'bearer', 'customHeader', 'queryParam'
      authSecretId: [''],
      authHeaderName: [''],
      authParamName: [''],
    });

    this.serviceForm.get('mode')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      applyConditionalValidators(this.serviceForm, SERVICE_VALIDATORS);
    });
    this.serviceForm.get('authType')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      applyConditionalValidators(this.serviceForm, SERVICE_VALIDATORS);
    });

    this.objectStorageForm.get('provider')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      const provider = this.objectStorageForm.get('provider')?.value as ObjectStorageProvider;
      const currentAuthType = this.objectStorageForm.get('authType')?.value as ObjectStorageAuthType;

      this.store.loadOAuthProvidersFor(provider);

      const allowedAuthTypes = this.getProviderAuthTypes(provider);
      if (!allowedAuthTypes.includes(currentAuthType)) {
        this.objectStorageForm.patchValue({ authType: allowedAuthTypes[0] });
      }

      // Provider-specific fields that are now hidden must not carry stale values
      // into the save payload.
      this.clearHiddenObjectStorageFields();

      applyConditionalValidators(this.objectStorageForm, OBJECT_STORAGE_VALIDATORS);
    });

    this.objectStorageForm.get('authType')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((authType) => {
      if (authType === ObjectStorageAuthType.OAuth) {
        this.store.loadOAuthCredentialsFor(this.objectStorageForm.get('provider')?.value as ObjectStorageProvider);
      }
      if (authType === ObjectStorageAuthType.ServerIdentity) {
        this.store.ensureStorageGrants();
      }
      applyConditionalValidators(this.objectStorageForm, OBJECT_STORAGE_VALIDATORS);
      this.revalidatePrefix();
    });

    // Choosing a grant fills in its bucket, and its prefix unless the current
    // one already lies inside it.
    this.objectStorageForm.get('storageGrantId')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((id) => {
      const grant = this.findGrant(id);
      if (!grant) {
        this.revalidatePrefix();
        return;
      }
      const prefix = this.objectStorageForm.get('prefix')?.value as string;
      this.objectStorageForm.patchValue({
        bucket: grant.bucket,
        prefix: grantCovers(grant, prefix) ? prefix : grant.prefix,
      });
      this.revalidatePrefix();
    });

    applyConditionalValidators(this.objectStorageForm, OBJECT_STORAGE_VALIDATORS);
    applyConditionalValidators(this.serviceForm, SERVICE_VALIDATORS);
  }

  onTabChange(index: number): void {
    this.selectedTabIndex.set(index);
    this.error.set(null);
    this.testResult.set(null);
  }

  onTypeChange(type: SupportedConnectionType | null): void {
    if (type === 'database') {
      this.selectedTabIndex.set(0);
    } else if (type === 'datafusion') {
      this.selectedTabIndex.set(1);
    } else if (type === 'llm') {
      this.selectedTabIndex.set(2);
    } else if (type === 'box') {
      this.selectedTabIndex.set(3);
    } else if (type === 'objectstorage') {
      this.selectedTabIndex.set(4);
      const provider = this.objectStorageForm.get('provider')?.value as ObjectStorageProvider;
      if (provider) {
        this.store.loadOAuthProvidersFor(provider);
      }
    } else if (type === 'service') {
      this.selectedTabIndex.set(5);
      this.store.ensureGlobalServices();
    } else {
      this.selectedTabIndex.set(null);
    }
    this.error.set(null);
  }

  private initializeEditMode(): void {
    const existing = this.data.connection;
    if (!existing) {
      return;
    }

    const description = 'description' in existing ? existing.description : undefined;

    if (this.isObjectStorageConnection(existing)) {
      this.connectionBasicsForm.patchValue({ type: 'objectstorage' });
      this.onTypeChange('objectstorage');
      this.objectStorageForm.patchValue(objectStorageConfigToForm(existing));
      // An edit can't change the provider or auth type; the server refuses it
      // (EHQ-460). Changing either means creating a new connection.
      this.objectStorageForm.get('provider')?.disable({ emitEvent: false });
      this.objectStorageForm.get('authType')?.disable({ emitEvent: false });
      // Set name and description after objectStorageForm is fully initialized
      // to ensure they are not overwritten by any form subscription side effects.
      this.connectionBasicsForm.patchValue({ name: existing.name, description: description ?? '' });
      if (existing.authType === ObjectStorageAuthType.OAuth) {
        this.store.loadOAuthCredentialsFor(existing.provider);
      }
      if (existing.authType === ObjectStorageAuthType.ServerIdentity) {
        this.store.ensureStorageGrants();
      }
      return;
    }

    this.connectionBasicsForm.patchValue({ name: existing.name, description: description ?? '' });

    const type = this.mapConnectionTypeToSupported(existing.type);
    this.connectionBasicsForm.patchValue({ type });
    this.onTypeChange(type);

    const config = existing.config as unknown as Record<string, unknown>;
    if (type === 'box') {
      // A registry-backed connection has boxAppId; a legacy one (inline creds,
      // pre-migration) does not — flag it so the form shows a deprecation notice.
      this.isLegacyBoxConnection.set(config['boxAppId'] == null);
    }

    const descriptor = STANDARD_CONNECTION_DESCRIPTORS[type as StandardConnectionKey];
    if (descriptor) {
      this.typeFormFor(type as StandardConnectionKey).patchValue(descriptor.fromConfig(config));
      if (type === 'service') {
        applyConditionalValidators(this.serviceForm, SERVICE_VALIDATORS);
      }
    }
  }

  private typeFormFor(key: StandardConnectionKey): FormGroup {
    const forms: Record<StandardConnectionKey, FormGroup> = {
      database: this.databaseForm,
      datafusion: this.dataFusionForm,
      llm: this.llmForm,
      box: this.boxForm,
      service: this.serviceForm,
    };
    return forms[key];
  }

  private isObjectStorageConnection(connection: AddConnectionDialogData['connection']): connection is ObjectStorageConnection {
    return !!connection && 'provider' in connection && 'authType' in connection && 'bucket' in connection;
  }

  private mapConnectionTypeToSupported(type: ConnectionType | string): SupportedConnectionType {
    switch (String(type).toLowerCase()) {
      case 'database':
        return 'database';
      case 'datafusion':
        return 'datafusion';
      case 'llm':
        return 'llm';
      case 'box':
        return 'box';
      case 'service':
        return 'service';
      default:
        return 'database';
    }
  }

  private getEditingConnectionId(): number | null {
    const id = this.data.connection?.connectionId;
    return id ? Number(id) : null;
  }

  private basics(): ConnectionBasics {
    return {
      name: this.connectionBasicsForm.value.name,
      description: this.connectionBasicsForm.value.description,
    };
  }

  onCancel(): void {
    this.onAttemptClose();
  }

  onAttemptClose(): void {
    if (!this.hasFormChanges()) {
      this.dialogRef.close();
      return;
    }
    this.confirmAbandonment(() => this.dialogRef.close());
  }

  onOpenSettings(): void {
    const openSettings = () => {
      this.dialogRef.close();
      this.dialog.open(ProjectSettingsPageComponent, {
        data: {
          projectId: this.data.projectId,
          currentVersion: this.appState.version(),
          currentUserId: this.appState.user()?.userId,
        },
        width: '1280px',
        maxWidth: '90vw',
        // Fixed height (not just maxHeight) so the dialog stays a constant size and
        // does not re-center/jump when switching tabs; tab content scrolls internally.
        height: '90vh',
        maxHeight: '90vh',
        disableClose: false,
      });
    };
    if (!this.hasFormChanges()) {
      openSettings();
      return;
    }
    this.confirmAbandonment(openSettings);
  }

  /** Open the OAuth authorization popup for the given provider config (delegated to the store). */
  onAuthorizeNew(providerConfigId: number): void {
    if (this.store.isInitiatingOAuth()) {
      return;
    }
    this.error.set(null);
    const displayName = this.connectionBasicsForm.get('name')?.value as string;
    this.store
      .authorize(providerConfigId, displayName)
      .then((result) => {
        if (result.credentialId != null) {
          this.objectStorageForm.patchValue({ userCredentialId: result.credentialId });
        } else if (result.error) {
          // Provider denied authorization — preserve the original inline error.
          this.error.set(`Authorization failed: ${result.error}`);
        }
        // A cancelled (popup-closed) result is intentionally silent.
      })
      .catch((err) => {
        console.error('Error initiating OAuth:', err);
        this.error.set('Failed to start authorization. Please try again.');
      });
  }

  /** Returns the credential label for display, including expiry info */
  getCredentialLabel(credential: OAuthCredential): string {
    const base = credential.displayName ?? `Credential #${credential.userStorageCredentialId}`;
    const expiresDate = parseServerDate(credential.expiresAt);
    if (expiresDate) {
      const isExpired = expiresDate < new Date();
      return isExpired
        ? `${base} (expired)`
        : `${base} (expires ${expiresDate.toLocaleDateString()})`;
    }
    return base;
  }

  isCredentialExpired(credential: OAuthCredential): boolean {
    const expiresDate = parseServerDate(credential.expiresAt);
    if (!expiresDate) return false;
    return expiresDate < new Date();
  }

  onSaveDatabase(): void {
    if (!this.connectionBasicsForm.valid || !this.databaseForm.valid) {
      return;
    }
    this.commitStandardSave(
      'database',
      this.databaseForm,
      this.isEditMode ? 'Failed to save database connection' : 'Failed to create database connection',
    );
  }

  onSaveDataFusion(): void {
    if (!this.connectionBasicsForm.valid || !this.dataFusionForm.valid) {
      return;
    }
    this.commitStandardSave('datafusion', this.dataFusionForm, 'Failed to save DataFusion connection');
  }

  onSaveLLM(): void {
    if (!this.connectionBasicsForm.valid || !this.llmForm.valid) {
      return;
    }
    this.commitStandardSave('llm', this.llmForm, 'Failed to save LLM connection');
  }

  onSaveBox(): void {
    if (!this.connectionBasicsForm.valid || !this.boxForm.valid) {
      return;
    }
    this.commitStandardSave('box', this.boxForm, 'Failed to save Box connection');
  }

  onSaveService(): void {
    if (!this.connectionBasicsForm.valid || !this.serviceForm.valid) {
      return;
    }
    this.commitStandardSave('service', this.serviceForm, 'Failed to save service connection');
  }

  private commitStandardSave(key: StandardConnectionKey, typeForm: FormGroup, errorMessage: string): void {
    this.isSaving.set(true);
    this.error.set(null);
    this.testResult.set(null);
    this.store
      .saveStandard(key, this.isEditMode, this.getEditingConnectionId(), this.basics(), typeForm.value)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => this.dialogRef.close(result),
        error: (err) => {
          console.error('Error saving connection:', err);
          this.error.set(errorMessage);
          this.isSaving.set(false);
        },
      });
  }

  onSaveObjectStorage(): void {
    if (!this.connectionBasicsForm.valid || !this.objectStorageForm.valid) {
      return;
    }
    this.isSaving.set(true);
    this.error.set(null);
    this.store
      .saveObjectStorage(this.isEditMode, this.getEditingConnectionId(), this.basics(), this.objectStorageForm.getRawValue())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => this.dialogRef.close(result),
        error: (err) => {
          console.error('Error saving Object Storage connection:', err);
          this.error.set('Failed to save Object Storage connection');
          this.isSaving.set(false);
        },
      });
  }

  onSaveConnection(): void {
    switch (this.selectedTabIndex()) {
      case 0:
        this.onSaveDatabase();
        break;
      case 1:
        this.onSaveDataFusion();
        break;
      case 2:
        this.onSaveLLM();
        break;
      case 3:
        this.onSaveBox();
        break;
      case 4:
        this.onSaveObjectStorage();
        break;
      case 5:
        this.onSaveService();
        break;
      default:
        break;
    }
  }

  getFormFieldError(form: FormGroup, fieldName: string): string | null {
    const field = form.get(fieldName);
    if (field?.invalid && field?.touched) {
      if (field.errors?.['required']) {
        return `${this.getFieldLabel(fieldName)} is required`;
      }
      if (field.errors?.['minlength']) {
        return `${this.getFieldLabel(fieldName)} is too short`;
      }
      if (field.errors?.['pattern']) {
        return `${this.getFieldLabel(fieldName)} has invalid format`;
      }
    }
    return null;
  }

  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      name: 'Name',
      type: 'Type',
      databaseType: 'Database Type',
      host: 'Host',
      port: 'Port',
      username: 'Username',
      dbname: 'Database',
      passwordSecretId: 'Secret',
      url: 'URL',
      endpoint: 'Endpoint',
      region: 'Region',
      bucket: 'Bucket',
      accessKey: 'Access Key',
      llmType: 'LLM Type',
      apiKeySecretId: 'API Key Secret',
      authType: 'Auth Type',
      clientId: 'Client ID',
      clientSecretId: 'Client Secret',
      enterpriseId: 'Enterprise ID',
      boxAppId: 'Box App',
      rootFolderId: 'Root Folder ID',
      provider: 'Provider',
      projectId: 'Project ID',
      accountName: 'Account Name',
      secretId: 'Secret',
      userCredentialId: 'OAuth Credential',
    };
    return labels[fieldName] || fieldName;
  }

  getProviderIcon(provider: ObjectStorageProvider): string {
    switch (provider) {
      case ObjectStorageProvider.S3:
        return 'connection-s3';
      case ObjectStorageProvider.GCS:
        return 'connection-gcs';
      case ObjectStorageProvider.AWS:
        return 'connection-aws';
      case ObjectStorageProvider.Azure:
        return 'connection-azure';
      default:
        return 'connection-object-storage';
    }
  }

  getProviderLabel(provider: ObjectStorageProvider): string {
    switch (provider) {
      case ObjectStorageProvider.S3:
        return 'S3';
      case ObjectStorageProvider.GCS:
        return 'Google Cloud Storage';
      case ObjectStorageProvider.AWS:
        return 'Amazon Web Services';
      case ObjectStorageProvider.Azure:
        return 'Azure Blob Storage';
      default:
        return String(provider);
    }
  }

  /**
   * Returns the auth types allowed for a given provider.
   * OAuth is appended when providers have been loaded from the backend.
   */
  /**
   * Whether a provider-specific object-storage field applies to the current
   * provider: endpoint+region for S3/AWS, projectId (GCS project id, optional)
   * for GCS, accountName for Azure. Matches OBJECT_STORAGE_VALIDATORS so the
   * form only shows fields the backend uses.
   */
  isObjectStorageFieldVisible(
    field: 'endpoint' | 'region' | 'projectId' | 'accountName',
  ): boolean {
    const provider = this.objectStorageForm.get('provider')
      ?.value as ObjectStorageProvider;
    switch (field) {
      case 'endpoint':
      case 'region':
        return (
          provider === ObjectStorageProvider.S3 ||
          provider === ObjectStorageProvider.AWS
        );
      case 'projectId':
        return provider === ObjectStorageProvider.GCS;
      case 'accountName':
        return provider === ObjectStorageProvider.Azure;
      default:
        return false;
    }
  }

  /** Blank out provider-specific fields that don't apply to the current provider. */
  private clearHiddenObjectStorageFields(): void {
    (['endpoint', 'region', 'projectId', 'accountName'] as const).forEach(
      (field) => {
        if (!this.isObjectStorageFieldVisible(field)) {
          this.objectStorageForm.get(field)?.setValue('', { emitEvent: false });
        }
      },
    );
  }

  getProviderAuthTypes(provider: ObjectStorageProvider): ObjectStorageAuthType[] {
    let baseTypes: ObjectStorageAuthType[];
    switch (provider) {
      case ObjectStorageProvider.S3:
      case ObjectStorageProvider.AWS:
      case ObjectStorageProvider.Azure:
        baseTypes = [ObjectStorageAuthType.AccessKey];
        break;
      case ObjectStorageProvider.GCS:
        // Keyless (EHQ-427) is GCS-only; the dialog itself is admin-only.
        baseTypes = [ObjectStorageAuthType.ServiceAccount, ObjectStorageAuthType.ServerIdentity];
        break;
      default:
        baseTypes = [ObjectStorageAuthType.AccessKey];
    }

    if (this.store.oauthProviders().length > 0) {
      baseTypes = [...baseTypes, ObjectStorageAuthType.OAuth];
    }

    return baseTypes;
  }

  getAuthTypeLabel(authType: ObjectStorageAuthType): string {
    switch (authType) {
      case ObjectStorageAuthType.AccessKey:
        return 'Access Key';
      case ObjectStorageAuthType.ServiceAccount:
        return 'Service Account';
      case ObjectStorageAuthType.OAuth:
        return 'OAuth';
      case ObjectStorageAuthType.ServerIdentity:
        return 'Keyless (Enlighten service account)';
      default:
        return String(authType);
    }
  }

  get isOAuthSelected(): boolean {
    return this.objectStorageForm.get('authType')?.value === ObjectStorageAuthType.OAuth;
  }

  get isKeylessSelected(): boolean {
    return this.objectStorageForm.get('authType')?.value === ObjectStorageAuthType.ServerIdentity;
  }

  /** The approved grant chosen for a Keyless connection, if any. */
  get selectedGrant(): StorageGrant | undefined {
    return this.findGrant(this.objectStorageForm.get('storageGrantId')?.value);
  }

  /** Why a Keyless connection's prefix can't be used, or null when it fits its grant. */
  get keylessPrefixProblem(): string | null {
    const grant = this.selectedGrant;
    if (!grant || !this.objectStorageForm.get('prefix')?.hasError('outsideGrant')) {
      return null;
    }
    return `Must be ${grant.prefix || 'the bucket'} or a prefix under it.`;
  }

  /**
   * The prefix must lie inside the chosen grant (EHQ-427). A real validator, so
   * the field shows a mat-error and is aria-invalid, and the form is invalid.
   */
  private keylessPrefixValidator(control: AbstractControl): ValidationErrors | null {
    if (!this.objectStorageForm || !this.isKeylessSelected) {
      return null;
    }
    const grant = this.selectedGrant;
    return grant && !grantCovers(grant, control.value as string) ? { outsideGrant: true } : null;
  }

  /** The prefix rule depends on the auth type and grant, so re-run it when they change. */
  private revalidatePrefix(): void {
    const prefix = this.objectStorageForm.get('prefix');
    prefix?.updateValueAndValidity({ emitEvent: false });
    if (prefix?.invalid) {
      prefix.markAsTouched();
    }
  }

  grantLabel(grant: StorageGrant): string {
    return grantLocation(grant);
  }

  private findGrant(id: unknown): StorageGrant | undefined {
    if (id === '' || id === null || id === undefined) {
      return undefined;
    }
    return this.store.approvedStorageGrants().find((g) => g.storageGrantId === Number(id));
  }

  get currentForm(): FormGroup {
    switch (this.selectedTabIndex()) {
      case 0:
        return this.databaseForm;
      case 1:
        return this.dataFusionForm;
      case 2:
        return this.llmForm;
      case 3:
        return this.boxForm;
      case 4:
        return this.objectStorageForm;
      case 5:
        return this.serviceForm;
      default:
        return this.connectionBasicsForm;
    }
  }

  get canSave(): boolean {
    return this.connectionBasicsForm.valid && this.currentForm.valid && !this.isSaving() && !this.isTesting() && this.selectedTabIndex() !== null;
  }

  get canTest(): boolean {
    return this.connectionBasicsForm.valid && this.currentForm.valid && !this.isSaving() && !this.isTesting() && this.selectedTabIndex() !== null;
  }

  get isTestingObjectStorage(): boolean {
    return this.selectedTabIndex() === 4;
  }

  onTestConnection(): void {
    if (!this.canTest) {
      return;
    }

    const projectId = this.data.projectId;
    const connectionId = this.getEditingConnectionId();

    if (this.isTestingObjectStorage) {
      const name = this.connectionBasicsForm.get('name')?.value as string;
      this.isTesting.set(true);
      this.error.set(null);
      this.testResult.set(null);
      this.store
        .testObjectStorage(name, this.objectStorageForm.getRawValue())
        .pipe(takeUntil(this.destroy$))
        .subscribe((result) => {
          this.isTesting.set(false);
          this.testResult.set({ success: result.success, message: result.message ?? '' });
        });
      return;
    }

    if (this.isEditMode && connectionId) {
      const name = (this.connectionBasicsForm.get('name')?.value as string) ?? 'Connection';
      this.featureDialogService.connectionTestingDialog({ connectionName: name, projectId, connectionId }).subscribe();
    } else {
      this.error.set('Save the connection first before testing.');
    }
  }

  private snapshotFormState(): void {
    this.initialFormSnapshot = this.captureFormSnapshot();
  }

  private captureFormSnapshot(): string {
    return JSON.stringify({
      basics: this.connectionBasicsForm.value,
      database: this.databaseForm.value,
      dataFusion: this.dataFusionForm.value,
      llm: this.llmForm.value,
      box: this.boxForm.value,
      objectStorage: this.objectStorageForm.value,
      service: this.serviceForm.value,
    });
  }

  private hasFormChanges(): boolean {
    if (this.initialFormSnapshot === null) {
      return true;
    }
    return this.captureFormSnapshot() !== this.initialFormSnapshot;
  }

  private confirmAbandonment(onConfirm: () => void): void {
    this.featureDialogService.confirmDialog({
      title: 'Leave connection setup?',
      message: 'The information you\'ve entered won\'t be saved.\nTo continue, you\'ll need to create the required secret and start setting up this connection again.',
      confirmButtonText: 'Leave setup',
      cancelButtonText: 'Cancel',
      isDestructive: true,
    }).subscribe((confirmed) => {
      if (confirmed) {
        onConfirm();
      }
    });
  }
}
